// Compagnons : notifications de l'appareil en tête (activation ou aide à l'installation), code et lien d'invitation
// (partage natif ou copie), code de retour de la personne connectée, héros du monde (portrait, rencontré ou non, lieu, en ligne, état, PV), ma compagnie
// (inviter un héros présent et rencontré, quitter le groupe) et « Coin du feu », la discussion hors-jeu.
import { useEffect, useRef, useState } from "preact/hooks";
import type { ChatMessage, ClassId, ClientView, Id, LifeState, OtherCharacterView } from "../../shared/types";
import { codeDeRetour, lienDeConnexion } from "../../shared/retour";
import { inviteLink } from "../api";
import { pushToast, send, useAppState } from "../store";
import { NotifyButton } from "./NotifyButton";
import { Portrait } from "./Portrait";
import "./Companions.css";

export interface CompanionsProps { view: ClientView }

const CHAT_MAX = 500;
/** À partir de cette longueur, le compteur de caractères s'affiche. */
const COUNTER_FROM = 400;

const CLASS_LABEL: Record<ClassId, string> = {
  guerrier: "Guerrier", mage: "Mage", rodeur: "Rôdeur", clerc: "Clerc", voleur: "Voleur", barde: "Barde",
};

type Tone = "ok" | "gold" | "mana" | "danger" | "ember" | "muted";
const LIFE: Record<LifeState, { label: string; tone: Tone }> = {
  actif: { label: "En vie", tone: "ok" },
  en_route: { label: "En voyage", tone: "gold" },
  endormi: { label: "Endormi", tone: "mana" },
  agonie: { label: "À l'agonie", tone: "danger" },
  mort: { label: "Mort", tone: "muted" },
  egare: { label: "Égaré", tone: "ember" },
};

// Le contrat (OtherCharacterView) ne dit pas dans quel groupe est un héros : la projection doit fournir `partyId`.
type OtherWithParty = OtherCharacterView & { partyId?: Id | null };

interface HeroEntry { o: OtherCharacterView; inParty: boolean }

/** Membre affiché dans « Ma compagnie » (moi compris). */
interface Member {
  key: Id;
  name: string;
  classId: ClassId;
  level: number;
  color: string;
  sigil: string;
  lifeState: LifeState;
  hpRatio: number | null;
  online: boolean;
  isMe: boolean;
}

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;
/** Couleur venue du serveur : seulement un hexadécimal, sinon une couleur de repli du thème. */
function safeColor(c: string | null | undefined, fallback = "var(--gold)"): string {
  return c && HEX_RE.test(c) ? c : fallback;
}

const timeFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });
function formatTime(at: number): string {
  return Number.isFinite(at) ? timeFmt.format(new Date(at)) : "";
}

function hpPct(ratio: number | null): number | null {
  return ratio === null ? null : Math.round(Math.min(1, Math.max(0, ratio)) * 100);
}

/** Ordre d'affichage : compagnons d'abord, puis ceux présents, puis ceux déjà rencontrés. */
function rank(h: HeroEntry): number {
  return (h.inParty ? 4 : 0) + (h.o.sameLocation ? 2 : 0) + (h.o.met ? 1 : 0);
}

function whereText(o: OtherCharacterView): string {
  if (o.sameLocation) return "Ici, avec toi";
  if (o.locationName) return o.locationName;
  return o.lifeState === "en_route" ? "Sur la route" : "Lieu inconnu";
}

async function copyInvite(url: string, code: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    pushToast("Lien copié. Envoie-le à ta tablée !", "succes");
  } catch {
    pushToast(`Copie impossible. Donne-leur le code ${code}.`, "danger");
  }
}

export function Companions(props: CompanionsProps) {
  const { view } = props;
  const me = view.character;
  const myPartyId = me?.partyId ?? null;
  const inParty = (o: OtherCharacterView) => myPartyId !== null && (o as OtherWithParty).partyId === myPartyId;
  const heroes: HeroEntry[] = view.others
    .map((o) => ({ o, inParty: inParty(o) }))
    .sort((a, b) => rank(b) - rank(a) || a.o.name.localeCompare(b.o.name, "fr"));
  const party = heroes.filter((h) => h.inParty).map((h) => h.o);

  return (
    <div class="companions">
      <NotifyButton variant="carte" />
      <InviteBlock code={view.world.code} />
      <RecoveryBlock />
      <PartyBlock view={view} party={party} />
      <HeroesBlock heroes={heroes} canInvite={me !== null} />
      <Hearth view={view} />
    </div>
  );
}

function InviteBlock({ code }: { code: string }) {
  async function invite(): Promise<void> {
    const url = inviteLink(code);
    // navigator.share doit partir directement du geste (pas d'attente avant l'appel).
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "Veillée", text: "Rejoins notre monde…", url });
        return;
      } catch (err) {
        // Annulé par la personne : rien à faire. Autre échec : on retombe sur la copie.
        if (err instanceof DOMException && err.name === "AbortError") return;
      }
    }
    await copyInvite(url, code);
  }

  return (
    <section class="companions-invite" aria-labelledby="companions-invite-title">
      <h2 id="companions-invite-title" class="companions-title">Inviter des compagnons</h2>
      <p class="companions-invite-text">Partage ce code ou ce lien pour que d'autres rejoignent ton monde.</p>
      <div class="companions-code-box">
        <span class="companions-code-label">Code du monde</span>
        <strong class="companions-code">{code}</strong>
      </div>
      <button type="button" class="btn btn-primary companions-invite-btn" onClick={invite}>Inviter quelqu'un</button>
    </section>
  );
}

/**
 * Lien de connexion et code de retour de la personne connectée : ils lui font retrouver son héros depuis un autre appareil
 * ou l'icône de l'écran d'accueil.
 */
function RecoveryBlock() {
  const { session } = useAppState();
  const token = session?.token ?? null;
  const worldCode = session?.code ?? "";
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    setCode(null);
    if (!token || typeof crypto === "undefined" || !crypto.subtle) return; // hors HTTPS : pas de calcul possible
    let alive = true;
    codeDeRetour(token).then((c) => { if (alive) setCode(c); }, () => {});
    return () => { alive = false; };
  }, [token]);

  if (code === null) return null;

  async function copy(text: string, done: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      pushToast(done, "succes");
    } catch {
      pushToast("Copie impossible : note ton code de retour à la main.", "danger");
    }
  }

  return (
    <section class="companions-section companions-recovery" aria-labelledby="companions-recovery-title">
      <h2 id="companions-recovery-title" class="companions-title">Te connecter ailleurs</h2>
      <p class="companions-recovery-text">
        Ton lien de connexion ouvre ton héros sur un autre appareil. Dans l'appli installée, colle-le dans Rejoindre →
        « J'ai déjà un héros ici », ou tapes-y ton code de retour. Garde-les pour toi.
      </p>
      <div class="companions-recovery-row">
        <strong class="companions-recovery-code">{code}</strong>
        <button type="button" class="btn btn-ghost btn-small" onClick={() => void copy(code, "Code de retour copié.")}>Copier le code</button>
        <button
          type="button"
          class="btn btn-primary btn-small"
          onClick={() => void copy(lienDeConnexion(location.origin, worldCode, code), "Lien de connexion copié.")}
        >
          Copier le lien
        </button>
      </div>
    </section>
  );
}

function PartyBlock({ view, party }: { view: ClientView; party: OtherCharacterView[] }) {
  const me = view.character;
  if (me === null) {
    return (
      <section class="companions-section" aria-labelledby="companions-party-title">
        <h2 id="companions-party-title" class="companions-title">Ma compagnie</h2>
        <p class="companions-empty">Ton héros n'a pas encore pris la route.</p>
      </section>
    );
  }

  const members: Member[] = [
    {
      key: me.id, name: me.name, classId: me.classId, level: me.level,
      color: me.appearance.color, sigil: me.appearance.sigil, lifeState: me.lifeState,
      hpRatio: me.hpMax > 0 ? me.hp / me.hpMax : null, online: true, isMe: true,
    },
    ...party.map((o): Member => ({
      key: o.id, name: o.name, classId: o.classId, level: o.level,
      color: o.appearance.color, sigil: o.appearance.sigil, lifeState: o.lifeState,
      hpRatio: o.hpRatio, online: o.online, isMe: false,
    })),
  ];
  const inGroup = Boolean(me.partyId);

  return (
    <section class="companions-section" aria-labelledby="companions-party-title">
      <h2 id="companions-party-title" class="companions-title">Ma compagnie</h2>
      <ul class="companions-heroes">
        {members.map((m) => <MemberRow key={m.key} m={m} />)}
      </ul>
      {!inGroup && (
        <p class="companions-empty">Tu marches seul pour l'instant. Invite un héros déjà rencontré, s'il se trouve à tes côtés.</p>
      )}
      {inGroup && (
        <button type="button" class="btn btn-ghost companions-leave" onClick={() => send({ t: "party_leave" })}>
          Quitter la compagnie
        </button>
      )}
    </section>
  );
}

function HeroesBlock({ heroes, canInvite }: { heroes: HeroEntry[]; canInvite: boolean }) {
  return (
    <section class="companions-section" aria-labelledby="companions-heroes-title">
      <h2 id="companions-heroes-title" class="companions-title">
        Héros du monde <span class="companions-count">{heroes.length}</span>
      </h2>
      {heroes.length === 0 ? (
        <p class="companions-empty">Personne d'autre n'a encore franchi ton seuil. Partage le code pour inviter quelqu'un.</p>
      ) : (
        <ul class="companions-heroes">
          {heroes.map((h) => <HeroCard key={h.o.id} o={h.o} inParty={h.inParty} canInvite={canInvite} />)}
        </ul>
      )}
    </section>
  );
}

function HeroCard({ o, inParty, canInvite }: { o: OtherCharacterView; inParty: boolean; canInvite: boolean }) {
  const dead = o.lifeState === "mort";
  const hp = o.met ? hpPct(o.hpRatio) : null;
  const showInvite = canInvite && o.met && o.sameLocation && !inParty && !dead;

  return (
    <li class={`companions-hero${dead ? " companions-hero--dead" : ""}`} style={`--hero-color: ${safeColor(o.appearance.color, "var(--line)")}`}>
      <div class="companions-hero-portrait">
        <Portrait classId={o.classId} color={safeColor(o.appearance.color)} sigil={o.appearance.sigil} size={56} dead={dead} />
      </div>
      <div class="companions-hero-body">
        <div class="companions-hero-head">
          <Dot online={o.online} />
          <span class="companions-hero-name">{o.name}</span>
        </div>
        <p class="companions-hero-sub">{CLASS_LABEL[o.classId]}, niveau {o.level} · joué par {o.playerName}</p>
        <div class="companions-hero-tags">
          <StateChip state={o.lifeState} />
          {o.met && <span class="companions-met">Rencontré</span>}
          {inParty && <span class="companions-tag">Dans ta compagnie</span>}
        </div>
        {o.met ? (
          <>
            <p class="companions-hero-where"><span aria-hidden="true">📍</span> {whereText(o)}</p>
            {hp !== null && <HpBar pct={hp} />}
          </>
        ) : (
          <p class="companions-hero-unmet">Pas encore rencontré — quelque part dans le monde…</p>
        )}
      </div>
      {showInvite && (
        <button
          type="button"
          class="btn btn-primary companions-hero-action"
          onClick={() => send({ t: "party_invite", characterId: o.id })}
        >
          Inviter dans ma compagnie
        </button>
      )}
    </li>
  );
}

function MemberRow({ m }: { m: Member }) {
  const dead = m.lifeState === "mort";
  const hp = hpPct(m.hpRatio);
  return (
    <li class={`companions-hero companions-hero--compact${dead ? " companions-hero--dead" : ""}`} style={`--hero-color: ${safeColor(m.color, "var(--line)")}`}>
      <div class="companions-hero-portrait">
        <Portrait classId={m.classId} color={safeColor(m.color)} sigil={m.sigil} size={44} dead={dead} />
      </div>
      <div class="companions-hero-body">
        <div class="companions-hero-head">
          <Dot online={m.online} />
          <span class="companions-hero-name">{m.name}</span>
          {m.isMe && <span class="companions-tag">Toi</span>}
        </div>
        <p class="companions-hero-sub">{CLASS_LABEL[m.classId]}, niveau {m.level}</p>
        <div class="companions-hero-tags">
          <StateChip state={m.lifeState} />
        </div>
        {hp !== null && <HpBar pct={hp} />}
      </div>
    </li>
  );
}

function Dot({ online }: { online: boolean }) {
  const label = online ? "En ligne" : "Hors ligne";
  return <span class={`companions-dot${online ? " companions-dot--on" : ""}`} role="img" aria-label={label} title={label} />;
}

function StateChip({ state }: { state: LifeState }) {
  const { label, tone } = LIFE[state];
  return <span class={`companions-state companions-state--${tone}`}>{label}</span>;
}

function HpBar({ pct }: { pct: number }) {
  return (
    <div class="companions-hp-row">
      <span class="companions-hp-label" aria-hidden="true">PV</span>
      <div
        class="companions-hp"
        role="meter"
        aria-label="Points de vie"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={`${pct} %`}
      >
        <span class="companions-hp-fill" style={`width: ${pct}%`} />
      </div>
    </div>
  );
}

function Hearth({ view }: { view: ClientView }) {
  const listRef = useRef<HTMLDivElement>(null);
  // Reste collé en bas tant que la personne ne remonte pas dans l'historique.
  const pinned = useRef(true);
  const [draft, setDraft] = useState("");

  const chat = view.chat;
  const last = chat.length > 0 ? chat[chat.length - 1] : null;
  const lastId = last?.id ?? null;
  const lastIsMine = last !== null && last.playerId === view.me.playerId;

  useEffect(() => {
    const el = listRef.current;
    if (el && (pinned.current || lastIsMine)) el.scrollTop = el.scrollHeight;
  }, [chat.length, lastId]);

  function submit(e: Event): void {
    e.preventDefault();
    const text = draft.slice(0, CHAT_MAX).trim();
    if (text === "") return;
    send({ t: "chat", text });
    setDraft("");
    pinned.current = true;
  }

  return (
    <section class="companions-hearth" aria-labelledby="companions-hearth-title">
      <h2 id="companions-hearth-title" class="companions-title">
        <span class="companions-flame" aria-hidden="true">🔥</span> Coin du feu
      </h2>
      <p class="companions-hearth-intro">La discussion hors-jeu de toute la tablée. Ici, on parle de tout, sauf des dés.</p>
      <div
        class="companions-chat"
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label="Messages du coin du feu"
        onScroll={(e) => {
          const el = e.currentTarget;
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {chat.length === 0 ? (
          <p class="companions-chat-empty">Le feu crépite. Personne n'a encore pris la parole : lance la conversation.</p>
        ) : (
          chat.map((m) => <ChatBubble key={m.id} m={m} mine={m.playerId === view.me.playerId} />)
        )}
      </div>
      <form class="companions-composer" onSubmit={submit}>
        <input
          class="input companions-input"
          type="text"
          value={draft}
          maxLength={CHAT_MAX}
          placeholder="Dis quelque chose au coin du feu…"
          aria-label="Ton message"
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="send"
          onInput={(e) => setDraft(e.currentTarget.value)}
        />
        <button type="submit" class="btn btn-primary companions-send" disabled={draft.trim() === ""} aria-label="Envoyer le message">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path d="M3 11.5 21 3l-8.5 18-2.2-7.3L3 11.5Z" fill="currentColor" />
          </svg>
        </button>
      </form>
      {draft.length >= COUNTER_FROM && (
        <p class={`companions-counter${draft.length >= CHAT_MAX ? " companions-counter--max" : ""}`}>
          {draft.length} / {CHAT_MAX}
        </p>
      )}
    </section>
  );
}

function ChatBubble({ m, mine }: { m: ChatMessage; mine: boolean }) {
  return (
    <div class={`companions-msg${mine ? " companions-msg--mine" : ""}`}>
      <div class="companions-msg-head">
        <span class="companions-msg-name" style={`color: ${safeColor(m.color)}`}>{m.name}</span>
        <time class="companions-msg-time">{formatTime(m.at)}</time>
      </div>
      <p class="companions-msg-text">{m.text}</p>
    </div>
  );
}
